import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { Button } from 'src/components/atoms';
import { IconButton, ListItemText, Menu, MenuItem } from '@mui/material';
import MoreIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/More.js';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import {
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules/Toast/Toast';
import { DeleteDraftConfirmModal } from 'src/components/pages/CoiFormManagement/DeleteDraftConfirmModal';
import { InfoModal } from 'src/components/atlas/molecules/InfoModal';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import { useDeleteFormManagementQuestionnaireMutation } from 'src/graphql';
import { useFormType } from '../../hooks';
import { FormCreationMode } from '../../types';
import {
  BackButton,
  HeaderActionsGroup,
  HeaderLeading,
  HeaderLeftSection,
  HeaderPrimaryButton,
  HeaderPublishIcon,
  HeaderSecondaryButton,
  HeaderTitle,
  HeaderTitleContainer,
  InfoIconButton,
  PageHeaderContainer,
} from './Header.styles';

interface Props {
  mode: FormCreationMode;
  onBackClick: () => void;
  onSave: () => void;
  onPublish: () => void;
  onPreview: () => void;
  isSaving: boolean;
  isPublishing: boolean;
  isActiveForm?: boolean;
  questionnaireId?: string | null;
  onDeleteSuccess?: () => void;
  onDuplicateClick?: () => void;
  onArchiveClick?: () => void;
  onAuditLogClick?: () => void;
  isEditMode?: boolean;
  duplicateManagementSessionActive?: boolean;
  saveDraftDisabled?: boolean;
  publishDisabled?: boolean;
  previewDisabled?: boolean;
}

export const Header: React.FC<Props> = ({
  mode,
  onBackClick,
  onSave,
  onPublish,
  onPreview,
  isSaving,
  isPublishing,
  isActiveForm = false,
  questionnaireId,
  onDeleteSuccess,
  onDuplicateClick,
  onArchiveClick,
  onAuditLogClick,
  isEditMode = false,
  duplicateManagementSessionActive = false,
  saveDraftDisabled = false,
  publishDisabled = false,
  previewDisabled = false,
}) => {
  const theme = useTheme();
  const formType = useFormType();
  const isSpeakUp = formType === FormType.SPEAK_UP;

  const effectiveIsEditMode = isEditMode || mode === 'edit';
  const showActivePublishedEditOverflow = mode === 'edit' && isActiveForm;
  const showCreateStyleHeaderRow =
    mode === 'content' || (mode === 'edit' && !isActiveForm);

  const [showDeleteDraftModal, setShowDeleteDraftModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [suggestedReadingOpen, setSuggestedReadingOpen] = useState(false);
  const isMenuOpen = Boolean(menuAnchorEl);

  const { mutate: deleteQuestionnaire, isLoading: isDeleting } =
    useDeleteFormManagementQuestionnaireMutation();

  const isPerformingAction = isSaving || isPublishing || isDeleting;
  const hasDraft = !!questionnaireId;
  const canOpenAuditLog = Boolean(questionnaireId);

  const DELETE_DRAFT_ERROR = {
    toastMessage: 'Deleting Draft failed',
    toastDescription:
      "We couldn't delete your draft. Please try again or refresh the page.",
  };

  const headerTitle =
    duplicateManagementSessionActive && !effectiveIsEditMode
      ? 'Duplicate form'
      : effectiveIsEditMode
        ? 'Edit form'
        : isSpeakUp
          ? 'Create speak up form'
          : 'Create conflict of interest form';

  const saveDraftLabel = isSaving
    ? 'Saving...'
    : mode === 'edit'
      ? duplicateManagementSessionActive
        ? 'Save draft'
        : 'Save changes'
      : 'Save draft';

  const publishLabel = isPublishing ? 'Publishing...' : 'Publish form';
  const useCompactSaveButton =
    saveDraftLabel === 'Save draft' || saveDraftLabel === 'Saving...';
  const useCompactPublishButton =
    publishLabel === 'Publish form' || publishLabel === 'Publishing...';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handlePreview = () => {
    handleMenuClose();
    onPreview();
  };

  const handleDuplicate = () => {
    handleMenuClose();
    if (onDuplicateClick) {
      onDuplicateClick();
    }
  };

  const handleArchive = () => {
    handleMenuClose();
    if (onArchiveClick) {
      onArchiveClick();
    }
  };

  const handleAuditLog = () => {
    handleMenuClose();
    if (onAuditLogClick) {
      onAuditLogClick();
    }
  };

  const handleDeleteDraft = () => {
    handleMenuClose();
    setShowDeleteDraftModal(true);
  };

  const handleDeleteDraftConfirm = () => {
    setShowDeleteDraftModal(false);

    deleteQuestionnaire(
      { id: questionnaireId! },
      {
        onSuccess: (data) => {
          const response = data.deleteFormManagementQuestionnaire;

          if (response?.success === false) {
            handleErrorToast(DELETE_DRAFT_ERROR);
            return;
          }

          handleSuccessToast({
            toastMessage: 'Deleting Draft was successful',
            toastDescription: 'Draft deleted successfully.',
          });
          if (onDeleteSuccess) {
            onDeleteSuccess();
          }
        },
        onError: () => {
          handleErrorToast(DELETE_DRAFT_ERROR);
        },
      },
    );
  };

  return (
    <>
      <PageHeaderContainer data-testid="form-creation-header">
        <HeaderLeftSection data-testid="form-creation-header-left-section">
          <HeaderLeading>
            <BackButton
              variant="tertiary"
              noBorder
              size="medium"
              fontSize="24px"
              iconName="uil uil-arrow-left"
              onClick={onBackClick}
              aria-label="Back"
              data-testid="form-creation-back-button"
            />
          </HeaderLeading>
          <HeaderTitleContainer>
            <HeaderTitle data-testid="form-creation-header-title">
              {headerTitle}
            </HeaderTitle>
            <InfoIconButton
              type="button"
              aria-label={`${headerTitle} information`}
              onClick={() => setSuggestedReadingOpen(true)}
              data-testid="form-creation-header-info-button"
            >
              <StyledIcon
                className="uil uil-info-circle"
                color={theme.atlas.semantic.color.type.default}
                size="24px"
                isClickable
              />
            </InfoIconButton>
          </HeaderTitleContainer>
        </HeaderLeftSection>
        <HeaderActionsGroup data-testid="form-creation-header-actions-group">
          {(mode === 'content' || mode === 'edit') && (
            <>
              {(showCreateStyleHeaderRow ||
                showActivePublishedEditOverflow) && (
                <IconButton
                  onClick={handleMenuOpen}
                  aria-label="More actions"
                  aria-expanded={isMenuOpen}
                  aria-haspopup="menu"
                  aria-controls={
                    isMenuOpen ? 'form-creation-actions-menu' : undefined
                  }
                  data-testid="form-creation-more-actions-button"
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '12px',
                  }}
                >
                  <MoreIcon aria-hidden />
                </IconButton>
              )}
              {showActivePublishedEditOverflow && (
                <Button
                  variant="secondary"
                  size="medium"
                  data-testid="form-creation-preview-button"
                  onClick={handlePreview}
                  disabled={previewDisabled}
                >
                  <StyledIconInButton
                    size="medium"
                    position="left"
                    className="uil uil-eye"
                  />
                  Preview
                </Button>
              )}
              {showCreateStyleHeaderRow && (
                <HeaderSecondaryButton
                  variant="secondary"
                  size="medium"
                  noMinWidth
                  noTextWrap
                  $compact={useCompactSaveButton}
                  data-testid="form-creation-save-button"
                  onClick={onSave}
                  disabled={
                    isActiveForm || isPerformingAction || saveDraftDisabled
                  }
                >
                  {saveDraftLabel}
                </HeaderSecondaryButton>
              )}
              <HeaderPrimaryButton
                variant="primary"
                size="medium"
                noMinWidth
                noTextWrap
                $compact={useCompactPublishButton}
                data-testid="form-creation-publish-button"
                onClick={onPublish}
                disabled={isPerformingAction || publishDisabled}
              >
                <HeaderPublishIcon
                  size="medium"
                  position="left"
                  className="uil uil-check"
                />
                {publishLabel}
              </HeaderPrimaryButton>
            </>
          )}
        </HeaderActionsGroup>

        <Menu
          id="form-creation-actions-menu"
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ list: { 'aria-label': 'More actions' } }}
          data-testid="form-creation-actions-menu"
        >
          {mode === 'edit' && (
            <>
              <MenuItem
                onClick={handleDuplicate}
                data-testid="form-creation-menu-duplicate"
                disabled={isPerformingAction || !onDuplicateClick}
                divider
              >
                <ListItemText>Duplicate</ListItemText>
              </MenuItem>
              {isActiveForm && onArchiveClick && (
                <MenuItem
                  onClick={handleArchive}
                  data-testid="form-creation-menu-archive"
                  disabled={isPerformingAction}
                  divider
                >
                  <ListItemText>Archive</ListItemText>
                </MenuItem>
              )}
              <MenuItem
                onClick={handleAuditLog}
                data-testid="form-creation-menu-audit-log"
                disabled={!canOpenAuditLog}
                divider
              >
                <ListItemText>Audit log</ListItemText>
              </MenuItem>
              {!isActiveForm && (
                <>
                  <MenuItem
                    onClick={handlePreview}
                    data-testid="form-creation-menu-preview"
                    disabled={previewDisabled}
                    divider
                  >
                    <ListItemText>Preview</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={handleDeleteDraft}
                    color="destructive"
                    data-testid="form-creation-menu-delete-draft"
                    disabled={!hasDraft || isPerformingAction}
                  >
                    <ListItemText>
                      {isDeleting ? 'Deleting...' : 'Delete draft'}
                    </ListItemText>
                  </MenuItem>
                </>
              )}
            </>
          )}
          {mode === 'content' && (
            <>
              <MenuItem
                onClick={handleAuditLog}
                data-testid="form-creation-menu-audit-log"
                disabled={!canOpenAuditLog}
                divider
              >
                <ListItemText>Audit log</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handlePreview}
                data-testid="form-creation-menu-preview"
                disabled={previewDisabled}
                divider
              >
                <ListItemText>Preview</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleDeleteDraft}
                color="destructive"
                data-testid="form-creation-menu-delete-draft"
                disabled={!hasDraft || isActiveForm || isPerformingAction}
              >
                <ListItemText>
                  {isDeleting ? 'Deleting...' : 'Delete draft'}
                </ListItemText>
              </MenuItem>
            </>
          )}
        </Menu>
      </PageHeaderContainer>

      <DeleteDraftConfirmModal
        open={showDeleteDraftModal}
        setOpen={setShowDeleteDraftModal}
        onCancel={() => setShowDeleteDraftModal(false)}
        onConfirm={handleDeleteDraftConfirm}
        isLoading={isDeleting}
        data-testid="delete-draft-modal"
      />
      <InfoModal
        open={suggestedReadingOpen}
        onClose={() => setSuggestedReadingOpen(false)}
        title="Suggested reading"
        links={[
          { label: 'How to use the form builder' },
          { label: 'Conditional logic' },
        ]}
        bodyText="Experiencing an issue or unable to locate what you're looking for? Reach out via Technical Support and we'll be happy to assist you."
      />
    </>
  );
};
